import styles from './hastaPublicaArrematacaoForm.module.scss';
import { BrDivider, BrInput, BrLoading, BrSelect, BrTextarea, Col, Row } from '@govbr-ds/react-components';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { useBackEndRequests } from '../../../../../lib/backEndRequests';
import { IMunicipio, IProcessoMinerario, ISubstancia } from '../../../../../../../interfaces/IProcessos';
import { IArrematacao } from '../../../../../../../interfaces/IArrematacao';
import { estadosUFOptions } from '../../../../../../../utils/estadosUFLista';
import { useNavigate } from 'react-router-dom';
import { useNotificacao } from '../../../../../../../hooks/useNotificacao';
import { formatarNrProcesso } from '../../../../../../../utils/formatNrProcesso';
import InfoCircleTooltip from '../../../../../../../components/infoCircleTooltip/infoCircleTooltip';
import { onTextAreaChange } from '../../../../../../../utils/textAreaSize';

interface IHastaPublicaArrematacaoFormProps {
  numeroProcesso: string;
  setNumeroProcesso: React.Dispatch<React.SetStateAction<string>>;
  substancia: string;
  setSubstancia: React.Dispatch<React.SetStateAction<string>>;
  municipio: IMunicipio;
  setMunicipio: React.Dispatch<React.SetStateAction<IMunicipio>>;
  unidadeMedida: string;
  setUnidadeMedida: React.Dispatch<React.SetStateAction<string>>;
  setIsWithError: React.Dispatch<React.SetStateAction<boolean>>;
  edicaoArrematacao: IArrematacao | undefined;
  hasProcessoMinerario: boolean;
  setHasProcessoMinerario: React.Dispatch<React.SetStateAction<boolean>>;
  isDadoMinerarioSalvo: boolean;
  observacoes: string;
  setObservacoes: React.Dispatch<React.SetStateAction<string>>;
}

function HastaPublicaArrematacaoForm({
  numeroProcesso,
  setNumeroProcesso,
  substancia,
  setSubstancia,
  municipio,
  setMunicipio,
  unidadeMedida,
  setUnidadeMedida,
  setIsWithError,
  edicaoArrematacao,
  hasProcessoMinerario,
  setHasProcessoMinerario,
  isDadoMinerarioSalvo,
  observacoes,
  setObservacoes,
}: Readonly<IHastaPublicaArrematacaoFormProps>) {
  const [numeroProcessoFeedbackText, setNumeroProcessoFeedbackText] = useState('');
  const [numeroProcessoStatus, setNumeroProcessoStatus] = useState<
    'success' | 'danger' | 'info' | 'warning' | undefined
  >(undefined);

  const navigate = useNavigate();
  const mostrarNotificacao = useNotificacao();
  const [isProcessoPreenchido, setIsProcessoPreenchido] = useState(false);
  const [isSubstanciaPreenchida, setIsSubstanciaPreenchida] = useState(false);
  const [municipioLabel, setMunicipioLabel] = useState('');

  const [estadoUF, setEstadoUF] = useState('');

  const [isEditingLoading, setIsEditingLoading] = useState(false);

  const { getProcessosData, getMunicipioPorUf, getSubstancias, getMunicipioByCodigoIBGE } = useBackEndRequests();

  const [municipios, setMunicipios] = useState<IMunicipio[]>([]);
  const [substancias, setSubstancias] = useState([
    {
      label: '',
      value: '',
    },
  ]);
  const [unidadesMedida, setUnidadesMedida] = useState([
    {
      label: '',
      value: '',
    },
  ]);

  const cleanFields = () => {
    setEstadoUF('');
    setMunicipios([]);
    setMunicipio({ nome: '', codigoIBGE: '' });
    setSubstancias([]);
    setSubstancia('');
    setUnidadesMedida([]);
    setUnidadeMedida('');
    setIsSubstanciaPreenchida(false);
    setMunicipioLabel('');
  };

  const {
    data: processoData,
    isLoading: isProcessoDataLoading,
    error: processoDataError,
  } = useQuery<IProcessoMinerario, IErrorWithSentryId>({
    queryKey: ['processoHastaPublicaQuery', numeroProcesso],
    queryFn: () => getProcessosData(numeroProcesso.replace(/[./]/g, '')),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: numeroProcesso.length === 12,
  });

  const {
    data: municipiosPorUfData,
    isLoading: isMunicipiosPorUfLoading,
    error: municipiosPorUfError,
  } = useQuery<IMunicipio[], IErrorWithSentryId>({
    queryKey: ['municipioPorUfQuery', estadoUF],
    queryFn: () => getMunicipioPorUf(estadoUF),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: !hasProcessoMinerario && !!estadoUF,
  });

  const {
    data: substanciasData,
    isLoading: isSubstanciasLoading,
    error: substanciasError,
  } = useQuery<ISubstancia[], IErrorWithSentryId>({
    queryKey: ['municipioPorUfQuery'],
    queryFn: () => getSubstancias(),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: !hasProcessoMinerario,
  });

  const onSubstanciaChange = (value: string, unidadeMedida?: string) => {
    setSubstancia(value);
    let substancia;

    if (hasProcessoMinerario) {
      substancia = processoData?.substancias.find((item) => item.nome === value);
    } else {
      substancia = substanciasData?.find((item) => item.nome === value);
    }

    const unidadesMedida = substancia
      ? substancia.unidadesMedida.map((unidade) => ({
          label: unidade, // Unidade como label
          value: unidade, // Unidade como value (pode ajustar se necessário)
        }))
      : [];

    setUnidadesMedida(unidadesMedida);

    unidadeMedida && edicaoArrematacao !== undefined
      ? setUnidadeMedida(unidadeMedida)
      : setUnidadeMedida(unidadesMedida[0]?.value);

    setIsSubstanciaPreenchida(true);
  };

  useEffect(() => {
    if (processoData) {
      setIsProcessoPreenchido(true);
      setNumeroProcessoFeedbackText('');
      setNumeroProcessoStatus(undefined);
      setIsWithError(false);

      const municipios = processoData.municipios;

      setMunicipios(municipios);

      let municipioSelecionado = null;

      if (municipio?.codigoIBGE !== '' && edicaoArrematacao !== undefined) {
        municipioSelecionado = municipios.find((m) => m.codigoIBGE.toString() === municipio.codigoIBGE.toString());
      }

      municipioSelecionado ??= municipios[0];

      setMunicipio(municipioSelecionado);
      setMunicipioLabel(`${municipioSelecionado.nome}/${municipioSelecionado.uf}`);

      const substancias = processoData.substancias.map((substancias) => ({
        label: substancias.nome, // Nome do município para o campo 'label'
        value: substancias.nome, // Código IBGE para o campo 'value'
      }));

      setSubstancias(substancias);

      if (edicaoArrematacao !== undefined) {
        onSubstanciaChange(substancia, unidadeMedida);
      } else {
        onSubstanciaChange(substancias[0].value);
      }
    } else if ((processoData !== undefined && !processoData) || processoDataError?.status === 404) {
      setIsProcessoPreenchido(false);
      setIsSubstanciaPreenchida(false);
      setNumeroProcessoFeedbackText('Processo não encontrado');
      setNumeroProcessoStatus('danger');
      setIsWithError(true);
    }
  }, [processoData, processoDataError]);

  useEffect(() => {
    if (municipiosPorUfData) {
      const municipios = municipiosPorUfData;
      if (isEditingLoading) {
        setIsEditingLoading(false);
      } else {
        setMunicipioLabel('');
      }
      setMunicipios(municipios);
    }
  }, [municipiosPorUfData, municipiosPorUfError, substanciasData]);

  useEffect(() => {
    if (substanciasData && !hasProcessoMinerario) {
      const substancias = substanciasData.map((substancia) => ({
        label: substancia.nome, // Nome da substância para o campo 'label'
        value: substancia.nome, // Nome da substância para o campo 'value'
      }));
      setSubstancias(substancias);
    }
  }, [substanciasData, substanciasError, hasProcessoMinerario]);

  useEffect(() => {
    if (!!edicaoArrematacao && !hasProcessoMinerario && !!municipio) {
      setIsEditingLoading(true);
      onSubstanciaChange(substancia, unidadeMedida);
      getMunicipioByCodigoIBGE(municipio.codigoIBGE)
        .then((res) => {
          setMunicipioLabel(`${res.nome}/${res.uf}`);
          setMunicipio({
            nome: res.nome,
            uf: res.uf,
            codigoIBGE: res.codigoIBGE,
          });
          setEstadoUF(res.uf || '');
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: 'Erro ao editar',
            ...(err && { complementoSentryId: err }),
          });
          navigate(0);
        });
    }
  }, [hasProcessoMinerario]);

  const formatarMunicipio = function () {
    if (municipio) {
      if (hasProcessoMinerario) return `${municipio.nome}/${municipio.uf}`;
      else return municipio.nome;
    } else {
      return '';
    }
  };

  const changeNumeroProcesso = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setNumeroProcesso(event.target.value);
    if (event.target.value.length === 0) {
      setNumeroProcessoFeedbackText('Campo obrigatório');
      setNumeroProcessoStatus('danger');
      setIsWithError(true);
    } else if (event.target.value.length < 12) {
      cleanFields();
    } else {
      setNumeroProcessoFeedbackText('');
      setNumeroProcessoStatus(undefined);
      setIsWithError(false);
    }
  };

  const onObservacaoChange = function (value: string) {
    onTextAreaChange(value, observacoes, setObservacoes);
  };

  return (
    <div className={styles.container}>
      {(isProcessoDataLoading || isMunicipiosPorUfLoading || isEditingLoading || isSubstanciasLoading) && (
        <div className={styles.loadContainer}>
          <BrLoading large label='Carregando...' />
        </div>
      )}
      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>Há número de processo minerário?</span>
          {!isDadoMinerarioSalvo && (
            <p className={styles.textoAuxiliar}>
              Escolha "Não" caso não possua um número de processo minerário relacionado a arrematação.
            </p>
          )}
        </Col>
        <Col className={styles.dadosCol}>
          {!isDadoMinerarioSalvo ? (
            <div
              className={`br-switch icon`}
              role='presentation'
              onClick={() => {
                setNumeroProcesso('');
                setEstadoUF('');
                cleanFields();
                setHasProcessoMinerario(!hasProcessoMinerario);
              }}
            >
              <input
                id='switch-icon'
                type='checkbox'
                name='switch-icon'
                checked={hasProcessoMinerario}
                onChange={(e) => setHasProcessoMinerario(e.target.checked)}
                role='switch'
                disabled={edicaoArrematacao !== undefined && hasProcessoMinerario && !!municipio}
              />
              <label className={styles.labelSwitch}>{hasProcessoMinerario ? 'Sim' : 'Não'}</label>
            </div>
          ) : (
            <span className={styles.dadoAdicionado}>{hasProcessoMinerario ? 'Sim' : 'Não'}</span>
          )}
        </Col>
      </Row>
      <Row className={styles.row}>
        {hasProcessoMinerario && (
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Nº do Processo Minerário</span>
            {!isDadoMinerarioSalvo ? (
              <BrInput
                autoComplete='off'
                mask='###.###/####'
                value={numeroProcesso}
                onChange={(e) => changeNumeroProcesso(e)}
                feedbackText={numeroProcessoFeedbackText}
                status={numeroProcessoStatus}
                disabled={edicaoArrematacao !== undefined && hasProcessoMinerario && !!municipio}
              />
            ) : (
              <span className={styles.dadoAdicionado}>{formatarNrProcesso(numeroProcesso)}</span>
            )}
          </Col>
        )}
        {!hasProcessoMinerario && (
          <Col sm={3} className={styles.dadosCol}>
            <div className={styles.label}>
              Estado (UF){' '}
              <div className={styles.tooltip}>
                <InfoCircleTooltip
                  text='Informe a UF correspondente ao local de extração (origem) do minério sempre que houver comprovação documental ou verificação técnica pela autarquia. Na impossibilidade de identificar a origem exata por meios documentais ou técnicos, informe a UF onde ocorreu a efetiva apreensão do bem mineral.'
                  id='tooltipUF'
                />
              </div>
            </div>
            {!isDadoMinerarioSalvo ? (
              <BrSelect
                className={styles.select}
                options={estadosUFOptions}
                value={estadoUF}
                onChange={(value: string) => {
                  setEstadoUF(value);
                  !hasProcessoMinerario && setMunicipio({ nome: '', codigoIBGE: '' });
                }}
              />
            ) : (
              <span className={styles.dadoAdicionado}>{estadoUF}</span>
            )}
          </Col>
        )}
        <Col className={styles.dadosCol}>
          <div className={styles.label}>
            Município{hasProcessoMinerario && '/UF'}{' '}
            {!hasProcessoMinerario && (
              <div className={styles.tooltip} style={{ left: '65px' }}>
                <InfoCircleTooltip
                  text='Informe o município correspondente ao local de extração (origem) do minério sempre que houver comprovação documental ou verificação técnica pela autarquia. Na impossibilidade de identificar a origem exata por meios documentais ou técnicos, informe o município onde ocorreu a efetiva apreensão do bem mineral.'
                  id='tooltipMunicipio'
                />
              </div>
            )}
          </div>
          {!isDadoMinerarioSalvo ? (
            <BrSelect
              className={styles.select}
              options={(Array.isArray(municipios) ? municipios : []).map((municipio) => ({
                label: hasProcessoMinerario ? `${municipio.nome}/${municipio.uf}` : municipio.nome,
                value: municipio.codigoIBGE.toString(),
              }))}
              disabled={hasProcessoMinerario ? !isProcessoPreenchido : !estadoUF}
              value={municipio.codigoIBGE.toString()}
              placeholder={municipioLabel}
              onChange={(value: string) => {
                const selectedMunicipio = municipios.find((m) => m.codigoIBGE.toString() === value);
                if (selectedMunicipio) {
                  setMunicipio(selectedMunicipio);
                }
              }}
            />
          ) : (
            <span className={styles.dadoAdicionado}>{formatarMunicipio()}</span>
          )}
        </Col>
        <Col className={styles.dadosCol}>
          <span className={styles.label}>Substância</span>
          {!isDadoMinerarioSalvo ? (
            <BrSelect
              className={styles.select}
              options={substancias}
              disabled={hasProcessoMinerario ? !isProcessoPreenchido : !substanciasData}
              value={substancia}
              placeholder={substancia}
              onChange={(value: string) => onSubstanciaChange(value)}
            />
          ) : (
            <span className={styles.dadoAdicionado}>{substancia}</span>
          )}
        </Col>
      </Row>
      <Row className={styles.row}>
        <Col className={styles.dadosCol}>
          <span className={styles.label}>Uni. de Medida</span>
          {!isDadoMinerarioSalvo ? (
            <BrSelect
              className={styles.select}
              options={unidadesMedida}
              disabled={hasProcessoMinerario ? !isProcessoPreenchido : !isSubstanciaPreenchida}
              value={unidadeMedida}
              placeholder={unidadeMedida}
              onChange={(value: string) => setUnidadeMedida(value)}
            />
          ) : (
            <span className={styles.dadoAdicionado}>{unidadeMedida}</span>
          )}
        </Col>
        <Col></Col>
        <Col></Col>
      </Row>
      <div className={styles.dividerContainer}>
        <BrDivider />
      </div>
      <Row>
        <Col>
          <span className={styles.tooltipLabel}>
            <InfoCircleTooltip
              text='Outras observações pertinentes a quaisquer aquisições que envolvem a tríade processo minerário, substância e município preenchida acima.'
              id='observacoes'
            />
            Observações
          </span>
        </Col>
        <Col>
          <div className={styles.observacoesContainer}>
            <BrTextarea density='large' value={observacoes} onChange={(e) => onObservacaoChange(e.target.value)} />
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default HastaPublicaArrematacaoForm;
